import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/Clientes.css';
import { reporteService } from '../services/api';

const PrestamosAtrasados = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { codigo } = useParams();

  const getRoute = (path) => (codigo ? `/${codigo}${path}` : path);

  useEffect(() => {
    loadPrestamos();
  }, []);

  const loadPrestamos = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await reporteService.getPrestamosAtrasados();

      setPrestamos(response.data.prestamos || []);
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Error al cargar los préstamos');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const normalizeTipo = (tipo) =>
    (tipo || 'otros').toString().trim().toLowerCase();

  const capitalTypes = new Set(['capital_final', 'capital_final_fijo']);

  const capitalPorTipo = prestamos.reduce((acc, prestamo) => {
    const tipo = normalizeTipo(prestamo.tipo);
    if (!capitalTypes.has(tipo)) return acc;
    if (!acc[tipo]) acc[tipo] = [];
    acc[tipo].push(prestamo);
    return acc;
  }, {});

  const capitalIds = new Set(
    Object.values(capitalPorTipo)
      .flat()
      .map((prestamo) => String(prestamo.id))
  );

  const prestamosPorTipo = prestamos.reduce((acc, prestamo) => {
    const tipo = normalizeTipo(prestamo.tipo);
    if (capitalTypes.has(tipo)) {
      return acc;
    }
    if (capitalIds.has(String(prestamo.id))) {
      return acc;
    }
    if (!acc[tipo]) {
      acc[tipo] = [];
    }
    acc[tipo].push(prestamo);
    return acc;
  }, {});

  const tipoLabels = {
    capital_final: 'Préstamos capital final',
    capital_final_fijo: 'Préstamos capital final fijo',
    tipo_san: 'Préstamos tipo SAN',
    amortizado: 'Préstamos amortizados',
    amortizado_frances: 'Préstamos amortizados francés',
    cuota_fija: 'Préstamos cuota fija'
  };

  const ordenTipos = [
    'capital_final',
    'capital_final_fijo',
    'tipo_san',
    'amortizado',
    'amortizado_frances',
    'cuota_fija'
  ];

  const tiposCapital = Object.keys(capitalPorTipo);
  const tiposAmortizados = Object.keys(prestamosPorTipo);

  const tiposExtrasCapital = tiposCapital.filter(
    (tipo) => !ordenTipos.includes(tipo)
  );
  const tiposExtrasAmortizados = tiposAmortizados.filter(
    (tipo) => !ordenTipos.includes(tipo)
  );

  const tiposCapitalParaMostrar = [...ordenTipos, ...tiposExtrasCapital].filter(
    (tipo, index, self) =>
      self.indexOf(tipo) === index && capitalPorTipo[tipo]?.length
  );

  const tiposAmortizadosParaMostrar = [
    ...ordenTipos,
    ...tiposExtrasAmortizados
  ].filter(
    (tipo, index, self) =>
      self.indexOf(tipo) === index && prestamosPorTipo[tipo]?.length
  );

  const getTipoLabel = (tipo) => {
    const tipoNormalizado = normalizeTipo(tipo);
    if (tipoLabels[tipoNormalizado]) return tipoLabels[tipoNormalizado];
    const formateado = tipoNormalizado.replace(/_/g, ' ');
    return `Préstamos ${formateado}`;
  };

  const irAPlanPagos = (prestamoId) => {
    if (!prestamoId) return;
    navigate(getRoute(`/pagos?opcion=plan&prestamoId=${prestamoId}`));
  };

  const renderTablaCapital = (tipo, lista) => (
    lista.length === 0 ? null :
    <div className="card" key={tipo} style={{ marginBottom: '2rem' }}>
      <div className="card-title" style={{ background: '#f39c12' }}>
        {getTipoLabel(tipo)}
      </div>

      {lista.length === 0 ? (
        <p style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
          Ningún dato disponible en esta tabla
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Préstamo / Cliente</th>
                <th>Monto</th>
                <th>Capital</th>
                <th>Interés</th>
                <th>Vencidas</th>
                <th>Modalidad</th>
                <th>Dirección</th>
                <th>Negocio</th>
                <th>Celular</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((prestamo) => (
                <tr key={prestamo.id}>
                  <td>
                    {prestamo.id ? (
                      <button
                        type="button"
                        onClick={() => irAPlanPagos(prestamo.id)}
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          margin: 0,
                          color: '#007bff',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                        title="Ver reporte de pago del préstamo"
                      >
                        #{prestamo.id}
                      </button>
                    ) : (
                      <span style={{ color: '#dc3545', fontWeight: 600 }}>Sin ID</span>
                    )}{' '}
                    {prestamo.cliente?.nombres} {prestamo.cliente?.apellidos}
                  </td>
                  <td>{formatMoney(prestamo.monto_prestado)}</td>
                  <td>{formatMoney(prestamo.capital)}</td>
                  <td>{formatMoney(prestamo.interes)}</td>
                  <td>{(prestamo.cuotas || 0) - (prestamo.cuotas_pagadas || 0)}</td>
                  <td>{prestamo.modalidad}</td>
                  <td>{prestamo.cliente?.direccion || 'N/A'}</td>
                  <td>{prestamo.cliente?.negocio || 'N/A'}</td>
                  <td>{prestamo.cliente?.celular || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderTablaAmortizada = (tipo, lista) => {
    const filteredList = lista || [];

    if (filteredList.length === 0) {
      return null;
    }

    return (
      <div className="card" key={tipo} style={{ marginBottom: '2rem' }}>
        <div className="card-title" style={{ background: '#f39c12' }}>
          {getTipoLabel(tipo)}
        </div>

        {filteredList.length === 0 ? (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
            Ningún dato disponible en esta tabla
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Préstamo / Cliente</th>
                  <th>Monto</th>
                  <th>Capital</th>
                  <th>Interés</th>
                  <th>A saldar</th>
                  <th>Vencidas</th>
                  <th>Cuota</th>
                  <th>Modalidad</th>
                  <th>Dirección</th>
                  <th>Negocio</th>
                  <th>Celular</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((prestamo) => {
                  const cuotasTotales = prestamo.cuotas || 1;
                  const totalPrestamo = prestamo.total || 0;
                  const cuota =
                    cuotasTotales > 0 ? totalPrestamo / cuotasTotales : totalPrestamo;
                  const saldoCalculado =
                    totalPrestamo - cuota * (prestamo.cuotas_pagadas || 0);
                  const saldo = Math.max(0, saldoCalculado);

                  return (
                    <tr key={prestamo.id}>
                      <td>
                        {prestamo.id ? (
                          <button
                            type="button"
                            onClick={() => irAPlanPagos(prestamo.id)}
                            style={{
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              margin: 0,
                              color: '#007bff',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                            title="Ver reporte de pago del préstamo"
                          >
                            #{prestamo.id}
                          </button>
                        ) : (
                          <span style={{ color: '#dc3545', fontWeight: 600 }}>Sin ID</span>
                        )}{' '}
                        {prestamo.cliente?.nombres} {prestamo.cliente?.apellidos}
                      </td>
                      <td>{formatMoney(prestamo.monto_prestado)}</td>
                      <td>{formatMoney(prestamo.capital)}</td>
                      <td>{formatMoney(prestamo.interes)}</td>
                      <td>{formatMoney(saldo)}</td>
                      <td>{cuotasTotales - (prestamo.cuotas_pagadas || 0)}</td>
                      <td>{formatMoney(cuota)}</td>
                      <td>{prestamo.modalidad}</td>
                      <td>{prestamo.cliente?.direccion || 'N/A'}</td>
                      <td>{prestamo.cliente?.negocio || 'N/A'}</td>
                      <td>{prestamo.cliente?.celular || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Préstamos con cuotas vencidas</h1>
        <button
          className="btn"
          onClick={() => navigate(getRoute('/dashboard'))}
          style={{background: '#666', color: '#fff'}}
        >
          ← Volver al Dashboard
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}

      {tiposCapitalParaMostrar.map((tipo) =>
        renderTablaCapital(tipo, capitalPorTipo[tipo] || [])
      )}
      {tiposAmortizadosParaMostrar.map((tipo) =>
        renderTablaAmortizada(tipo, prestamosPorTipo[tipo] || [])
      )}
    </div>
  );
};

export default PrestamosAtrasados;
